import { ModalFuncProps } from 'antd';
import useModalMethod from './use-modal-method';
import { useEffect, useRef } from 'react';

interface GlobalModalParams extends Partial<ModalFuncProps> {
  key?: string;
  method: keyof ReturnType<typeof useModalMethod>;
}

export const globalModal = (params: GlobalModalParams) => {
  if (!params.method) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent('global-modal', {
      detail: params,
    }),
  );
};

const useGlobalModal = () => {
  const modal = useModalMethod();
  // 使用 ref 来跟踪已经显示的模态框
  const activeModalsRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const handleGlobalModal = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const { method, key, ...rest } = customEvent.detail;

        if (!modal[method]) {
          return;
        }

        // 如果有 key 并且该 key 的模态框已经显示，则不再重复显示
        if (key && activeModalsRef.current.has(key)) {
          return;
        }

        // 如果有 key，标记该 key 的模态框已经显示
        if (key) {
          activeModalsRef.current.set(key, true);

          // 当模态框关闭时，从 Map 中移除
          const onCancel = rest.onCancel;
          const onOk = rest.onOk;

          rest.onCancel = (...args: any[]) => {
            activeModalsRef.current.delete(key);
            onCancel?.(...args);
          };

          rest.onOk = (...args: any[]) => {
            activeModalsRef.current.delete(key);
            onOk?.(...args);
          };

          // 为了修改样式 加一个类名
          rest.className = rest.className
            ? `${rest.className} use-global-modal`
            : 'use-global-modal';
        }
        modal[method](rest);
      }
    };

    window.addEventListener('global-modal', handleGlobalModal as EventListener);

    return () => {
      window.removeEventListener(
        'global-modal',
        handleGlobalModal as EventListener,
      );
    };
  }, []);
};

export default useGlobalModal;
